// Modal de revisión de specs propuestas por IA antes de guardarlas al producto.
// Muestra specs ACTUALES (referencia) y las PROPUESTAS editables (agregar/editar/quitar).
// Al guardar, escribe el objeto editado a products.specs y limpia specs_proposed.

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { canonicalSpecKey } from '../../../lib/specLabels';

type Props = {
  productId: string;
  sku: string;
  name: string;
  onClose: (saved: boolean) => void;
};

type EditRow = { key: string; value: string; fromAI: boolean };

function toRows(current: Record<string, unknown>, proposed: Record<string, unknown>): EditRow[] {
  const merged: Record<string, EditRow> = {};
  // Primero specs actuales (se conservan).
  for (const k of Object.keys(current)) {
    const ck = canonicalSpecKey(k);
    merged[ck] = { key: ck, value: String(current[k] ?? ''), fromAI: false };
  }
  // Luego propuestas de IA (sobreescriben / agregan, marcadas).
  for (const k of Object.keys(proposed)) {
    const ck = canonicalSpecKey(k);
    merged[ck] = { key: ck, value: String(proposed[k] ?? ''), fromAI: true };
  }
  return Object.keys(merged).map(k => merged[k]);
}

export function SpecReviewModal({ productId, sku, name, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<EditRow[]>([]);
  const [model, setModel] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await supabase.from('products')
        .select('specs, specs_proposed, specs_proposed_model')
        .eq('id', productId).maybeSingle();
      const cur = (data?.specs && typeof data.specs === 'object') ? data.specs as Record<string, unknown> : {};
      const prop = (data?.specs_proposed && typeof data.specs_proposed === 'object') ? data.specs_proposed as Record<string, unknown> : {};
      setRows(toRows(cur, prop));
      setModel((data?.specs_proposed_model as string) ?? '');
      setLoading(false);
    })();
  }, [productId]);

  const aiCount = useMemo(() => rows.filter(r => r.fromAI).length, [rows]);

  const setRow = (i: number, patch: Partial<EditRow>) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  };
  const removeRow = (i: number) => setRows(prev => prev.filter((_, idx) => idx !== i));
  const addRow = () => setRows(prev => [...prev, { key: '', value: '', fromAI: false }]);

  const save = async () => {
    setSaving(true);
    setError('');
    const specs: Record<string, string> = {};
    for (const r of rows) {
      const k = r.key.trim();
      const v = r.value.trim();
      if (!k || !v) continue;
      specs[canonicalSpecKey(k)] = v;
    }
    const { error: upErr } = await supabase.from('products').update({
      specs,
      specs_proposed: null,
      specs_proposed_at: null,
      specs_proposed_model: null,
    }).eq('id', productId);
    setSaving(false);
    if (upErr) { setError(upErr.message); return; }
    onClose(true);
  };

  const discard = async () => {
    // Descartar propuesta sin tocar specs actuales.
    await supabase.from('products').update({
      specs_proposed: null, specs_proposed_at: null, specs_proposed_model: null,
    }).eq('id', productId);
    onClose(true);
  };

  return (
    <div style={overlay}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
              Revisar especificaciones · IA
            </div>
            <div style={{ fontSize: 17, fontWeight: 600, color: theme.brand, marginTop: 2 }}>{name}</div>
            <div style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{sku}</div>
          </div>
          <button onClick={() => onClose(false)} style={xBtn}>✕</button>
        </div>

        <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ color: theme.textMuted, fontSize: 13 }}>Cargando propuesta…</div>
          ) : (
            <>
              <div style={{
                padding: '10px 14px', background: theme.accentSoft, border: `1px solid ${theme.accentDark}`,
                fontSize: 12, color: theme.brand, marginBottom: 16, lineHeight: 1.5,
              }}>
                <strong>{aiCount}</strong> especificaciones propuestas por IA (marcadas con ✨). Revísalas, edita lo que haga falta y guarda. Nada se guarda hasta que confirmes.
                {model && <span style={{ color: theme.textMuted }}> · modelo {model}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 60px', gap: 8, marginBottom: 6, fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                <div>Especificación</div><div>Valor</div><div></div>
              </div>

              {rows.map((r, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 60px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                  <input value={r.key} onChange={e => setRow(i, { key: e.target.value })} placeholder="llave" style={inp} />
                  <input value={r.value} onChange={e => setRow(i, { value: e.target.value })} placeholder="valor" style={inp} />
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'flex-end' }}>
                    {r.fromAI && <span title="Propuesto por IA" style={{ fontSize: 13 }}>✨</span>}
                    <button onClick={() => removeRow(i)} style={delBtn} title="Quitar">✕</button>
                  </div>
                </div>
              ))}

              <button onClick={addRow} style={{ ...linkBtn, marginTop: 6 }}>+ Agregar especificación</button>

              {error && (
                <div style={{ marginTop: 14, padding: 10, background: '#FEE2E2', border: '1px solid #DC2626', color: '#991B1B', fontSize: 12 }}>{error}</div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '14px 22px', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={discard} style={{ ...linkBtn, color: theme.err }}>Descartar propuesta</button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => onClose(false)} style={ghostBtn}>Cancelar</button>
            <button onClick={save} disabled={saving || loading} style={{
              ...primaryBtn, opacity: (saving || loading) ? 0.6 : 1, cursor: (saving || loading) ? 'default' : 'pointer',
            }}>{saving ? 'Guardando…' : '✓ Guardar en producto'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};
const panel: React.CSSProperties = {
  background: 'white', width: 'min(680px, 96vw)', maxHeight: '90vh',
  display: 'flex', flexDirection: 'column', borderRadius: 4, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  fontFamily: theme.font.sans,
};
const inp: React.CSSProperties = {
  padding: '7px 10px', border: `1px solid ${theme.border}`, borderRadius: 2, fontSize: 12, fontFamily: theme.font.sans, width: '100%', boxSizing: 'border-box',
};
const xBtn: React.CSSProperties = { background: 'transparent', border: 'none', fontSize: 18, color: theme.textMuted, cursor: 'pointer' };
const delBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: 12 };
const linkBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: theme.brand, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font.sans, padding: 0 };
const ghostBtn: React.CSSProperties = { background: 'transparent', border: `1px solid ${theme.borderStrong}`, color: theme.text, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font.sans, borderRadius: 2 };
const primaryBtn: React.CSSProperties = { background: theme.brand, color: 'white', border: 'none', padding: '9px 18px', fontSize: 13, fontWeight: 700, fontFamily: theme.font.sans, borderRadius: 2 };
